import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function AddPatientDialog({ batches, orthodontists = [], reload }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [reg, setReg] = useState("");
  const [bracesKit, setBracesKit] = useState("");
  const [orthodontist, setOrthodontist] = useState("none");
  const [batch, setBatch] = useState("none");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/patients", {
        name,
        phone,
        register_number: reg || null,
        braces_kit: bracesKit || null,
        orthodontist: orthodontist === "none" ? null : orthodontist,
        batch: batch === "none" ? null : batch,
      });
      toast.success("Patient added to roster");
      setName("");
      setPhone("");
      setReg("");
      setBracesKit("");
      setOrthodontist("none");
      setBatch("none");
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-patient-button"
          className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-sm transition-all active:scale-[0.98]"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add Patient
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-patient-modal" className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">New Patient</DialogTitle>
          <DialogDescription>Add a patient to the running treatment roster.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="p-name">Full Name</Label>
            <Input
              id="p-name"
              data-testid="add-patient-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-phone">Phone Number</Label>
            <Input
              id="p-phone"
              data-testid="add-patient-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-reg">Register Number (optional)</Label>
            <Input
              id="p-reg"
              data-testid="add-patient-reg-input"
              value={reg}
              onChange={(e) => setReg(e.target.value)}
              placeholder="Auto-generated if empty"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p-braces">Braces Kit / Appliance</Label>
            <Input
              id="p-braces"
              data-testid="add-patient-braces-input"
              value={bracesKit}
              onChange={(e) => setBracesKit(e.target.value)}
              placeholder="e.g. Metal 0.022 MBT, Ceramic, Clear aligner"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Orthodontist</Label>
            <Select value={orthodontist} onValueChange={setOrthodontist}>
              <SelectTrigger data-testid="add-patient-ortho-select">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">Unassigned</SelectItem>
                {orthodontists.map((o) => (
                  <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Initial Batch</Label>
            <Select value={batch} onValueChange={setBatch}>
              <SelectTrigger data-testid="add-patient-batch-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="none">Unassigned</SelectItem>
                {batches.map((b) => (
                  <SelectItem key={b.label} value={b.label}>
                    Batch {b.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              data-testid="add-patient-form-submit"
              disabled={loading}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
            >
              {loading ? "Adding…" : "Add Patient"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
